import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';
import styles from './SubscriptionViewer.module.css';

const SubscriptionViewer = ({ subscription }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const popoverRef = useRef(null);
    const timeoutRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                popoverRef.current && 
                !popoverRef.current.contains(event.target) &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsHovered(false);
        }, 200); // Small delay to prevent flickering
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div 
            className={styles.container}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button
                ref={buttonRef}
                className={styles.infoButton}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                aria-label="View subscription details"
            >
                <Info size={14} />
            </button>

            {(isOpen || isHovered) && (
                <div 
                    className={styles.popover} 
                    ref={popoverRef}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <h4 className={styles.title}>Subscription Details</h4>

                    <div className={styles.detailRow}>
                        <span>Type:</span>
                        <span className={styles.value}>
                            {subscription?.type ?
                                subscription.type.charAt(0).toUpperCase() +
                                subscription.type.slice(1) :
                                'Free'}
                        </span>
                    </div>

                    {subscription?.type && (
                        <>
                            <div className={styles.detailRow}>
                                <span>Started:</span>
                                <span className={styles.value}>
                                    {formatDate(subscription.start_date)}
                                </span>
                            </div>

                            <div className={styles.detailRow}>
                                <span>Renews:</span>
                                <span className={styles.value}>
                                    {formatDate(subscription.end_date)}
                                </span>
                            </div>

                            <div className={styles.detailRow}>
                                <span>Status:</span>
                                <span className={`${styles.value} ${subscription.is_active ?
                                        styles.activeStatus :
                                        styles.expiredStatus
                                    }`}>
                                    {subscription.is_active ? 'Active' : 'Expired'}
                                </span>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default SubscriptionViewer;